// client/src/pages/Groups.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/chitconnect-logo-blue.png';
import { apiFetch, refreshSessionUser } from '../lib/api';
import { DateTime } from 'luxon';
import {
  formatDateAU,
  fmtLocal as fmtLocalDT,
  fmtGroup,
  fmtLocal,
  tzLabel,
  parseAnyDateTime,
} from '../lib/dates';
import { isApprovedOrganiser } from '../lib/roles';
import { formatMoneyForGroup } from '../lib/money';
import { downloadGroupSummaryPdf } from '../lib/groupSummaryPdf';
import BidHistoryModal from '../components/BidHistoryModal';

export default function Groups() {
  const navigate = useNavigate();

  const toInt = (v, def = 0) => {
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) ? n : def;
  };

  // start with cached user so UI paints quickly; then refresh once
  const cachedUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('user'));
    } catch {
      return null;
    }
  }, []);
  const [user, setUser] = useState(cachedUser);

  const [memberGroups, setMemberGroups] = useState([]);
  const [ownedGroups, setOwnedGroups] = useState([]);

  // Sorting uses server-provided next/active schedule metadata from:
  //  - GET /api/groups/my-home
  //  - GET /api/groups/owned-home
  // This avoids N+1 schedule/status calls (important on mobile).

  const [progressById, setProgressById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // expansion keyed per section to avoid opening both lists at once
  const [expanded, setExpanded] = useState({ member: null, owned: null });

  // active group context for modals (currency/country/timezone)
  const [activeGroup, setActiveGroup] = useState(null);

  // winners modal
  const [winnersOpen, setWinnersOpen] = useState(false);
  const [winnersLoading, setWinnersLoading] = useState(false);
  const [winners, setWinners] = useState([]);
  const [openProofKey, setOpenProofKey] = useState(null);
  // Lucky Dip evidence: show/hide calculation details per winner row
  const [openCalcKey, setOpenCalcKey] = useState(null);

  // schedule modal
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedLoading, setSchedLoading] = useState(false);
  const [schedRows, setSchedRows] = useState([]);
  const [schedTimezone, setSchedTimezone] = useState('');

  // Schedule times may arrive in different formats depending on environment
  // (ISO string, JS Date, MySQL SQL string, or occasionally already-formatted strings).
  // fmtGroup/fmtLocal return '—' if parsing fails; fall back to the raw value so
  // mobile cards never render as empty dashes.
  const fmtScheduleGroup = useCallback(
    (value, fmt = 'dd LLL yyyy, h:mm a') => {
      if (!value) return '—';
      const s = schedTimezone ? fmtGroup(value, schedTimezone, fmt) : fmtLocalDT(value, fmt);
      if (s && s !== '—') return s;
      return String(value);
    },
    [schedTimezone]
  );

  const fmtScheduleLocal = useCallback((value, fmt = 'dd LLL yyyy, h:mm a') => {
    if (!value) return '—';
    const s = fmtLocal(value, fmt);
    if (s && s !== '—') return s;
    return String(value);
  }, []);

  // bid history modal
  const [bhOpen, setBhOpen] = useState(false);
  const [bhLoading, setBhLoading] = useState(false);
  const [bhByPeriod, setBhByPeriod] = useState({}); // {periodNumber: [rows]}

  // roles
  const role = String(user?.access_level ?? user?.role ?? '').toLowerCase();
  const isSuperadmin = role === 'superadmin';
  const approvedOrganiser = isApprovedOrganiser(user);

  // refresh /me once so roles & approvals are fresh
  
  // No schedule prefetch needed: server already returns next/active window info.
useEffect(() => {
    (async () => {
      try {
        const fresh = await refreshSessionUser();
        if (fresh) setUser(fresh);
      } catch {}
    })();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    setError('');
    try {
      let [memberRes, ownedRes] = await Promise.all([
        apiFetch('/api/groups/my-home'),
        apiFetch('/api/groups/owned-home'),
      ]);

      // If the backend says the session is missing/expired, try a one-time session refresh then retry.
// (On mobile after heavy socket activity, cookies/token may not be hydrated at the exact moment this runs.)
if (memberRes.status === 401 || ownedRes.status === 401) {
  // One retry after refreshing session user (does NOT wipe storage)
  try { await refreshSessionUser(); } catch {}
  const [memberRes2, ownedRes2] = await Promise.all([
    apiFetch('/api/groups/my-home'),
    apiFetch('/api/groups/owned-home'),
  ]);

  if (memberRes2.status === 401 || ownedRes2.status === 401) {
    // Still unauthorized → redirect to login (don't aggressively clear tokens here; let login flow handle it)
    navigate('/login', { replace: true });
    return;
  }

  // Use the retried responses
  memberRes = memberRes2;
  ownedRes = ownedRes2;
}
const [memberData, ownedData] = await Promise.all([
        memberRes.json().catch(() => []),
        ownedRes.json().catch(() => []),
      ]);

      if (memberRes.ok) setMemberGroups(Array.isArray(memberData) ? memberData : []);
      else setError(memberData?.error || 'Failed to load member groups.');

      if (ownedRes.ok) setOwnedGroups(Array.isArray(ownedData) ? ownedData : []);
      else setError(ownedData?.error || 'Failed to load owned groups.');

      // No per-group status/schedule fetch here; backend endpoints already include next/active window metadata.


      // Fetch per-group progress (finished periods out of total duration)
      try {
        const progRes = await apiFetch('/api/groups/progress');
        const progData = await progRes.json().catch(() => ({}));
        if (progRes.ok) setProgressById(progData?.progressByGroupId || {});
      } catch {
        // ignore progress errors (do not block page)
      }
    } catch (err) {
      setError('Server error while fetching groups.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  // viewer local time (supports MySQL DATETIME and ISO)
  const fmtViewerLocal = (value) => fmtLocalDT(value, 'dd MMM yyyy, h:mm a');

  // --------------------------- group ordering ---------------------------
  // Sort groups by earliest bidding time (no extra API calls):
  //  1) Active window first
  //  2) Earliest next future start
  //  3) Fallback to auction_start_date
  const groupSortKeyMs = useCallback((group) => {
    const nowMs = Date.now();

    // 1) Active auctions first
    if (group?.auction_active || group?.is_active || group?.isAuctionActive) return nowMs - 1000;

    // 2) Next upcoming schedule start (server provides ISO UTC)
    const nextIso =
      group?.next_bid_start_time ||
      group?.nextBidStartTime ||
      group?.next_start_time ||
      group?.nextStartTime ||
      null;
    const nextMs = nextIso ? Date.parse(nextIso) : NaN;
    if (Number.isFinite(nextMs)) return nextMs;

    // 3) Fallback to group start date (last)
    const iso = group?.auction_start_date || null;
    const ms = iso ? Date.parse(iso) : Number.POSITIVE_INFINITY;
    return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
  }, []);

  const sortedMemberGroups = useMemo(() => {
    if (!Array.isArray(memberGroups) || memberGroups.length === 0) return [];
    return [...memberGroups].sort((a, b) => groupSortKeyMs(a) - groupSortKeyMs(b));
  }, [memberGroups, groupSortKeyMs]);

  const sortedOwnedGroups = useMemo(() => {
    if (!Array.isArray(ownedGroups) || ownedGroups.length === 0) return [];
    return [...ownedGroups].sort((a, b) => groupSortKeyMs(a) - groupSortKeyMs(b));
  }, [ownedGroups, groupSortKeyMs]);

  // lock body scroll while a modal is open
  useEffect(() => {
    // NOTE: keep this list explicit; do not reference undefined vars.
    const open = winnersOpen || schedOpen || bhOpen;
    const prev = document.body.style.overflow;
    if (open) document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [winnersOpen, schedOpen, bhOpen]);

  const pluralMap = { weekly: 'weeks', fortnightly: 'fortnights', monthly: 'months' };
  const toggleGroup = (section, groupId) =>
    setExpanded((prev) => ({ ...prev, [section]: prev[section] === groupId ? null : groupId }));

  const handleViewMembers = (groupId) => navigate(`/groups/${groupId}/members`);
  const handleApproveRequests = (groupId) => navigate(`/groups/${groupId}/approve`);
  const handleManageAuction = (groupId) => navigate(`/groups/${groupId}/auction`);
  const handleEditClick = (group) => navigate(`/groups/${group.id}/edit`);

  const parseProof = (p) => {
    try {
      return typeof p === 'string' ? JSON.parse(p) : p;
    } catch {
      return null;
    }
  };

  const handleShowWinners = async (group) => {
    const groupId = group?.id;
    setActiveGroup(group || null);

    setWinnersOpen(true);
    setWinnersLoading(true);
    try {
      const res = await apiFetch(`/api/bidding/winners/${groupId}`);
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data?.error || 'Failed to load winners');
      setWinners(Array.isArray(data) ? data : []);
    } catch (e) {
      alert(e.message || 'Failed to load winners');
      setWinners([]);
    } finally {
      setWinnersLoading(false);
    }
  };

  // Bidding schedule modal loader
  const handleShowSchedule = async (group) => {
    const groupId = group?.id;
    const groupTimezone = group?.timezone || '';
    setActiveGroup(group || null);

    setSchedTimezone(groupTimezone || '');
    setSchedOpen(true);
    setSchedLoading(true);
    setSchedRows([]);

    // Accept multiple API shapes and normalise to [{ period, start, end, startingBid }]
    const extractSchedule = (d) => {
      if (!d) return [];
      if (Array.isArray(d)) return d;
      if (Array.isArray(d.schedule)) return d.schedule;
      if (Array.isArray(d.rows)) return d.rows;
      if (Array.isArray(d.data)) return d.data;
      if (Array.isArray(d.items)) return d.items;
      if (Array.isArray(d.periods)) {
        return d.periods.map((p) => ({
          period_number: p.period_number ?? p.period ?? p.p ?? 0,
          start_time: p.start_time ?? p.start ?? p.startTime ?? null,
          end_time: p.end_time ?? p.end ?? p.endTime ?? null,
          starting_bid: p.starting_bid ?? p.startingBid ?? p.starting_bid_amount ?? p.startingBidAmount ?? null,
        }));
      }
      return [];
    };

    try {
      // Canonical schedule endpoint (Option A: remove legacy /api/auction and /api/bidding schedule aliases)
      const res = await apiFetch(`/api/groups/${groupId}/schedule`);
      const d = await res.json().catch(() => null);

      const rows = extractSchedule(d)
        .map((r) => {
          const period = Number(r.period_number ?? r.period ?? r.p ?? 0);
          const start = r.start_time ?? r.start ?? r.startTime ?? r.begin ?? null;
          const end = r.end_time ?? r.end ?? r.endTime ?? r.finish ?? null;
          const startingBid = r.starting_bid ?? r.startingBid ?? r.starting_bid_amount ?? r.startingBidAmount ?? null;
          return { period, start, end, startingBid };
        })
        .sort((a, b) => (a.period - b.period) || (new Date(a.start || 0) - new Date(b.start || 0)));

      setSchedRows(rows);
    } catch {
      setSchedRows([]);
    } finally {
      setSchedLoading(false);
    }
  };

  // Bid History (grouped by periods) — visible to all members/owners
  const handleShowBidHistory = async (group) => {
    const groupId = group?.id;
    setActiveGroup(group || null);

    setBhOpen(true);
    setBhLoading(true);
    setBhByPeriod({});

    // ---------- extractors ----------
    const extractRows = (d) => {
      if (!d) return [];
      if (Array.isArray(d)) return d;
      if (Array.isArray(d.history)) return d.history;
      if (Array.isArray(d.rows)) return d.rows;
      if (Array.isArray(d.data)) return d.data;
      if (Array.isArray(d.items)) return d.items;
      if (d.byPeriod && typeof d.byPeriod === 'object') {
        return Object.entries(d.byPeriod).flatMap(([pn, arr]) => (arr || []).map((r) => ({ ...r, period_number: r.period_number ?? pn })));
      }
      if (Array.isArray(d.periods)) {
        return d.periods.flatMap((p) => (p.bids || []).map((r) => ({ ...r, period_number: r.period_number ?? p.period_number })));
      }
      return [];
    };

    const extractSchedule = (d) => {
      if (!d) return [];
      const arr = Array.isArray(d)
        ? d
        : Array.isArray(d.schedule)
          ? d.schedule
          : Array.isArray(d.rows)
            ? d.rows
            : Array.isArray(d.data)
              ? d.data
              : Array.isArray(d.items)
                ? d.items
                : Array.isArray(d.periods)
                  ? d.periods
                  : [];

      return arr
        .map((r) => ({
          period_number: toInt(r.period_number ?? r.period ?? r.p ?? r.periodNo ?? r.periodIndex, 0),
          start_time: r.start_time ?? r.start ?? r.startTime ?? r.begin ?? null,
          end_time: r.end_time ?? r.end ?? r.endTime ?? r.finish ?? null,
        }))
        .filter((p) => p.period_number > 0 && p.start_time && p.end_time);
    };

    // ---------- optional name map for user_id -> display name ----------
    const nameById = {};
    try {
      const memberEndpoints = [`/api/groups/${groupId}/members`, `/api/groups/${groupId}`];
      for (const ep of memberEndpoints) {
        const r = await apiFetch(ep);
        const d = await r.json().catch(() => null);
        if (!r.ok || !d) continue;
        const members = Array.isArray(d) ? d : Array.isArray(d.members) ? d.members : [];
        for (const m of members) {
          const uid = m.user_id ?? m.id ?? m.userId;
          // Privacy: don't fall back to email for display names.
          const full =
            (m.full_name ?? [m.given_name, m.surname].filter(Boolean).join(' ')) ||
            m.name ||
            null;

          if (uid) nameById[uid] = full || `User #${uid}`;
        }
        if (Object.keys(nameById).length) break;
      }
    } catch {}

    // ---------- fetch history ----------
    let rows = [];
    try {
      const endpoints = [
        `/api/bidding/previous/${groupId}?limit=5000`,
        `/api/bidding/history/${groupId}?flat=1`,
        `/api/bidding/history/${groupId}`,
        `/api/bidding/bids/${groupId}`,
        `/api/bidding/bid-logs/${groupId}`,
        `/api/bidding/logs/${groupId}`,
      ];
      for (const ep of endpoints) {
        const res = await apiFetch(ep);
        const d = await res.json().catch(() => null);
        const pulled = extractRows(d);
        if (res.ok && Array.isArray(pulled)) {
          rows = pulled;
          break;
        }
      }
    } catch {
      rows = [];
    }

    // ---------- fetch schedule for period fallback ----------
    let schedule = [];
    try {
      const schedEndpoints = [`/api/groups/${groupId}/schedule`, `/api/bidding/schedule/${groupId}`, `/api/auction/schedule/${groupId}`];
      for (const ep of schedEndpoints) {
        const res = await apiFetch(ep);
        const d = await res.json().catch(() => null);
        const pulled = extractSchedule(d);
        if (res.ok && pulled.length) {
          schedule = pulled;
          break;
        }
      }
    } catch {}

    // Convert schedule to fast lookup
    const sched = schedule
      .map((p) => ({
        ...p,
        startMs: parseAnyDateTime(p.start_time)?.toMillis() ?? NaN,
        endMs: parseAnyDateTime(p.end_time)?.toMillis() ?? NaN,
      }))
      .sort((a, b) => a.period_number - b.period_number);

    // ---------- normalise rows ----------
    const norm = (rows || []).map((r) => {
      const pnRaw =
        r.period_number ??
        r.periodNumber ??
        r.period_no ??
        r.periodNo ??
        r.period_index ??
        r.periodIndex ??
        r.period ??
        r.p;

      const userId = r.user_id ?? r.userId ?? r.uid;
      const memIdx =
        r.membership_index ??
        r.membershipIndex ??
        r.membership ??
        r.member_index ??
        r.memberIndex ??
        r.slot ??
        r.mi;

      // first pass period
      let periodNum = toInt(pnRaw, 0);

      // fallback: derive from schedule by bid_time window
      if (!periodNum && r.bid_time && sched.length) {
        const t = parseAnyDateTime(r.bid_time)?.toMillis() ?? NaN;
        const hit = sched.find((p) => t >= p.startMs && t <= p.endMs);
        if (hit) periodNum = hit.period_number;
      }

      const name =
        r.user ??
        r.user_name ??
        r.member_name ??
        r.name ??
        (userId != null ? nameById[userId] || `User #${userId}` : '');

      return {
        period_number: periodNum || 0,
        amount:
          typeof r.amount === 'number'
            ? r.amount
            : typeof r.bid_amount === 'number'
              ? r.bid_amount
              : Number(r.amount) || 0,
        user: name,
        membership_index: memIdx != null ? toInt(memIdx, memIdx) : null,
        method: (() => {
          const m = String(r.method || (r.is_lucky ? 'lucky_dip' : r.is_winner ? 'bid' : r.type) || 'bid').toLowerCase();
          return (m === 'auto_bid' || m === 'manual' || m === 'socket') ? 'bid' : m;
        })(),
        bid_time: r.bid_time ?? r.time ?? r.created_at ?? r.ts ?? null,
      };
    });

    // sort & group
    norm.sort(
      (a, b) => a.period_number - b.period_number || new Date(a.bid_time || 0) - new Date(b.bid_time || 0)
    );

    const grouped = {};
    for (const row of norm) {
      const key = String(row.period_number || 0);
      (grouped[key] ||= []).push(row);
    }

    setBhByPeriod(grouped);
    setBhLoading(false);
  };

  // Has first bidding started?
  const hasAuctionsStarted = (g) => {
    try {
      if (!g?.auction_start_date) return false;

      const zone = g?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

      let dt = DateTime.fromISO(g.auction_start_date, { zone });
      if (!dt.isValid) return false;

      // If auction_start_date is a date-only, combine with group start_time (in group TZ)
      if (g?.start_time && dt.hour === 0 && dt.minute === 0) {
        const [hhRaw, mmRaw] = String(g.start_time).split(':');
        const hh = parseInt(hhRaw, 10);
        const mm = parseInt(mmRaw, 10);
        if (Number.isFinite(hh)) dt = dt.set({ hour: hh, minute: Number.isFinite(mm) ? mm : 0 });
      }

      return DateTime.utc() >= dt.toUTC();
    } catch {
      return false;
    }
  };


  const handleDownloadGroupPdf = async (group) => {
    try {
      setError('');
      await downloadGroupSummaryPdf(group.id, group.name, group?.timezone || null);
    } catch (e) {
      setError(e?.message || 'Failed to download group PDF');
    }
  };

  const renderGroupCard = (group, section) => {
    const frequency = group.contribution_frequency || 'monthly';
    const durationLbl = `${group.duration} ${pluralMap[frequency] || frequency + 's'}`;
    const isExpanded = expanded[section] === group.id;
    const isOwner = user?.id === group.owner_id;
    const canAdmin = isSuperadmin || isOwner;
    const started = hasAuctionsStarted(group);
    const progRow = progressById?.[group.id];
    const finishedPeriods = Number(progRow?.finished ?? 0);
    const uiStatus = (() => {
      const raw = String(group.status || '').toLowerCase();
      // If we already started/advanced, show Active even if DB status is still 'approved'
      if (raw === 'approved' && (started || finishedPeriods > 0)) return 'active';
      return group.status || 'approved';
    })();

    // Edit rule:
    // - Before first bidding: organiser + superadmin
    // - After first bidding: superadmin only
    const canEdit = isSuperadmin || (!started && isOwner);

    return (
      <div key={`${section}-${group.id}`} className={`collapsible-group ${isExpanded ? 'open' : ''}`}>
        <div className="collapsible-header" onClick={() => toggleGroup(section, group.id)}>
          <div>
            <h3>{group.name}</h3>
            <p>{group.description}</p>

            {(() => {
              const p = progressById?.[group.id];
              const finished = Number(p?.finished ?? 0);
              const total = Number(p?.total ?? group.duration ?? 0);
              const percent = Number(p?.percent ?? (total > 0 ? Math.round((finished / total) * 100) : 0));
              return (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12, opacity: 0.85 }}>
                    Progress: {finished} / {total} periods ({percent}%)
                  </div>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 999,
                      background: 'rgba(0,0,0,0.1)',
                      overflow: 'hidden',
                      marginTop: 4,
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.max(0, Math.min(100, percent))}%`,
                        borderRadius: 999,
                        background: 'rgba(0,0,0,0.35)',
                      }}
                    />
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="status-wrapper">
            <span className={`status-badge ${String(uiStatus).toLowerCase()}`}>{uiStatus}</span>
            <span className="dropdown-arrow">{isExpanded ? '▲' : '▼'}</span>
          </div>
        </div>

        {isExpanded && (
          <div className="collapsible-body">
            <div className="group-info-row">
              <p>
                <strong>Start Date:</strong> {formatDateAU(group.auction_start_date, group.timezone)}{' '}
              </p>
              <p>
                <strong>Duration:</strong> {durationLbl}
              </p>
              <p>
                <strong>{frequency.charAt(0).toUpperCase() + frequency.slice(1)}:</strong>{' '}
                {formatMoneyForGroup(group.contribution, group)}
              </p>
              <p>
                <strong>Organiser Benefit Period:</strong> {group.organiser_benefit_period}
              </p>
            </div>

            <div className="group-action-buttons-desktop">
              <button onClick={() => handleViewMembers(group.id)}>👥 Members</button>

              <button onClick={() => handleShowSchedule(group)}>📅 Bidding Schedule</button>

              {/* Premium-only: AutoBid (per membership). Show only if user has at least one premium membership in this group */}
              {Array.isArray(group.my_premium_memberships) && group.my_premium_memberships.length > 0 && (
                <button onClick={() => navigate(`/groups/${group.id}/autobid`)}>🤖 AutoBid</button>
              )}

              {/* Premium-only: Savings / Sacrifice Report */}
              {Array.isArray(group.my_premium_memberships) && group.my_premium_memberships.length > 0 && (
                <button onClick={() => navigate(`/groups/${group.id}/premium-report`)}>📈 Premium Report</button>
              )}

              {isOwner && <button onClick={() => handleDownloadGroupPdf(group)}>📄 Download Group PDF</button>}

              {/* Admin-only */}
              {canAdmin && !started && <button onClick={() => handleApproveRequests(group.id)}>✅ Approvals</button>}

              {/* Finalize winner (replaces old Auction button) */}
              {canAdmin && <button onClick={() => handleManageAuction(group.id)}>🏁 Finalize Winner</button>}

              <button onClick={() => handleShowWinners(group)}>🏆 Winners</button>
              <button onClick={() => handleShowBidHistory(group)}>📜 Bid History</button>

              {canEdit && <button onClick={() => handleEditClick(group)}>✏️ Edit</button>}

              {canAdmin && group.status === 'approved' && (
                <button onClick={() => navigate(`/groups/${group.id}/invite`)}>📨 Invite</button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const schedMoney = useCallback(
    (v) => formatMoneyForGroup(v, activeGroup, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    [activeGroup]
  );

  return (
    <div className="main-content">
      <img src={logo} alt="ChitConnect Logo" className="group-logo" />

      <h2>Groups I'm a Member Of</h2>
      {loading && <p>Loading groups…</p>}
      {error && <p className="error-text">{error}</p>}

      {!loading && memberGroups.length === 0 && <p>You haven't joined any groups yet.</p>}
      <div className="group-card-list">{sortedMemberGroups.map((g) => renderGroupCard(g, 'member'))}</div>

      <hr style={{ margin: '40px 0' }} />
      <h2>Groups I Own</h2>
      {!loading && ownedGroups.length === 0 && <p>You haven't created any groups yet.</p>}
      <div className="group-card-list">{sortedOwnedGroups.map((g) => renderGroupCard(g, 'owned'))}</div>

      <hr style={{ margin: '40px 0' }} />
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {isSuperadmin || approvedOrganiser ? (
          <button onClick={() => navigate('/groups/new')}>➕ Create New Group</button>
        ) : (
          <button onClick={() => navigate('/groups/organiser-request')}>📝 Request Organiser Approval</button>
        )}
      </div>

      {/* Winners Modal */}
      {winnersOpen && (
        <div
          onClick={() => setWinnersOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="winners-title"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 16,
              width: '96%',
              maxWidth: 760,
              maxHeight: '80vh',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0,0,0,.25)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <h3 id="winners-title" style={{ margin: 0 }}>
                Group Winners
              </h3>
              <button onClick={() => setWinnersOpen(false)} aria-label="Close winners">
                ✖
              </button>
            </div>
            <div
              className="winnersModalBody"
              style={{
                marginTop: 12,
                maxHeight: 'calc(80vh - 72px)',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
                paddingRight: 4,
              }}
            >


            {winnersLoading ? (
              <p style={{ marginTop: 12 }}>Loading…</p>
            ) : winners.length ? (
              <div className="winnersCards" style={{ marginTop: 12 }}>
                {winners
                  .slice()
                  .sort((a, b) => Number(a.period_number || 0) - Number(b.period_number || 0))
                  .map((row, i) => {
                    const isLucky =
                      String(row.method || '').toLowerCase().includes('lucky') ||
                      String(row.method || '').toLowerCase().includes('lucky_dip');

                    const isOrganiserBenefit =
                      String(row.method || '').toLowerCase().includes('organiser') ||
                      String(row.method || '').toLowerCase().includes('benefit');

                    // accept winner_label from backend
                    const winnerName = (row.user || row.winner_label || '').trim() || '—';

                    // prefer finalized_at; fall back to bid_time/created_at if ever added later
                    const finalizedISO = row.finalized_at || row.bid_time || row.created_at || null;

                    const proofObj = isLucky ? parseProof(row.proof_json || row.proofJson) : null;
                    const proofKey = `${row.period_number ?? 'x'}-${row.membership_index ?? 'x'}-${i}`;
                    const isProofOpen = openProofKey === proofKey;
                    const isCalcOpen = openCalcKey === proofKey;

                    const groupTz = activeGroup?.timezone || null;
                    const groupTime = finalizedISO
                      ? fmtGroup(finalizedISO, groupTz, "ccc d LLL yyyy, h:mm a")
                      : '—';
                    const yourTime = finalizedISO ? fmtLocal(finalizedISO, "ccc d LLL yyyy, h:mm a") : '—';
                    const groupTzLabel = tzLabel(groupTz);

                    const amountLabel = isOrganiserBenefit ? 'Benefit' : 'Winning bid';
                    const amountText =
                      row.amount != null && Number.isFinite(Number(row.amount))
                        ? formatMoneyForGroup(Number(row.amount), activeGroup)
                        : '—';

                    const methodLabel = isLucky
                      ? 'Lucky Dip'
                      : isOrganiserBenefit
                      ? 'Organiser benefit'
                      : row.method
                      ? String(row.method).replace(/_/g, ' ')
                      : 'Bid';

                    return (
                      <div key={proofKey} className="winnerCard">
                        <div className="winnerCardTop">
                          <div>
                            <div className="winnerCardTitle">
                              Period <strong>{row.period_number ?? '—'}</strong>
                            </div>
                            <div className="winnerCardTime">
                              {groupTime} <span className="winnerTz">({groupTzLabel})</span>
                              <div className="winnerCardYourTime">Your time: {yourTime}</div>
                            </div>
                          </div>

                          <div
                            className={
                              isLucky
                                ? 'winnerBadge winnerBadgeLucky'
                                : isOrganiserBenefit
                                ? 'winnerBadge winnerBadgeBenefit'
                                : 'winnerBadge'
                            }
                            title={methodLabel}
                          >
                            {methodLabel}
                          </div>
                        </div>

                        <div className="winnerCardBody">
                          <div className="winnerRow">
                            <span className="winnerKey">Winner</span>
                            <span className="winnerVal">
                              {winnerName}
                              {row.membership_index != null ? ` (Membership ${row.membership_index})` : ''}
                            </span>
                          </div>

                          <div className="winnerRow">
                            <span className="winnerKey">{amountLabel}</span>
                            <span className="winnerVal">{amountText}</span>
                          </div>

                          {row.prize != null && (
                            <div className="winnerRow">
                              <span className="winnerKey">Prize</span>
                              <span className="winnerVal">
                                {Number.isFinite(Number(row.prize))
                                  ? formatMoneyForGroup(Number(row.prize), activeGroup)
                                  : row.prize}
                              </span>
                            </div>
                          )}

                          {isLucky && (
                            <div className="winnerEvidence">
                              <button
                                type="button"
                                className="winnerEvidenceBtn"
                                onClick={() => {
                                  // Closing evidence should also close calculation details.
                                  if (isProofOpen) {
                                    setOpenProofKey(null);
                                    setOpenCalcKey(null);
                                  } else {
                                    setOpenProofKey(proofKey);
                                  }
                                }}
                                disabled={!proofObj}
                                title={proofObj ? 'View Lucky Dip evidence' : 'No evidence attached'}
                              >
                                {isProofOpen ? 'Hide evidence' : 'View evidence'}
                              </button>

                              {isProofOpen && proofObj && (
                                <div className="winnerEvidencePanel">
                                  <div className="winnerEvidenceExplainer">
                                    <div className="winnerEvidenceTitle">How Lucky Dip picked the winner</div>
                                    <div className="winnerEvidenceText">
                                      No one placed a bid for this period, so the system used <strong>Lucky Dip</strong> to keep the group moving.
                                      <br />
                                      <br />
                                      Every eligible membership is entered <strong>once</strong> into a draw in a fixed order:
                                      <strong> Member A</strong>, <strong>Member B</strong>, <strong>Member C</strong>, and so on.
                                      <br />
                                      <br />
                                      The system then takes a random value and converts it into a number. That number is used to select one entry fairly.
                                    </div>

                                    <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                      <button
                                        type="button"
                                        className="winnerEvidenceBtn"
                                        onClick={() => setOpenCalcKey(isCalcOpen ? null : proofKey)}
                                      >
                                        {isCalcOpen ? 'Hide Calculation Logic' : 'Calculation Logic'}
                                      </button>
                                    </div>

                                    {isCalcOpen && (
                                      <>
                                        {/* Eligible list */}
                                        <div style={{ marginTop: 12 }}>
                                          <div className="winnerEvidenceLabel">Eligible entries (N)</div>
                                          <div className="winnerEvidenceValue">
                                            {proofObj.eligible_count ??
                                              (proofObj.eligible_entries ? proofObj.eligible_entries.length : '—')}
                                          </div>

                                          <div className="winnerEvidenceList" style={{ marginTop: 8 }}>
                                            {(proofObj.eligible_entries || []).length ? (
                                              <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                {(proofObj.eligible_entries || []).map((e) => (
                                                  <li key={`${e.user_id}|${e.membership_index}`}>
                                                    <strong>{e.label}</strong>: {e.name}
                                                    {e.membership_index != null ? ` (Membership ${e.membership_index})` : ''}
                                                  </li>
                                                ))}
                                              </ul>
                                            ) : (
                                              <div className="winnerEvidenceMuted">
                                                (Eligible list not available for older results.)
                                              </div>
                                            )}
                                          </div>
                                        </div>

                                        {/* Calculation */}
                                        <div className="winnerEvidenceGrid" style={{ marginTop: 12 }}>
                                          <div className="winnerEvidenceLabel">Random source</div>
                                          <div className="winnerEvidenceValue">{proofObj.seed_source || '—'}</div>

                                          {proofObj.seed_source === 'drand' && (
                                            <>
                                              <div className="winnerEvidenceLabel">Drand round</div>
                                              <div className="winnerEvidenceValue">{proofObj.drand_round ?? '—'}</div>

                                              <div className="winnerEvidenceLabel">Random value</div>
                                              <div className="winnerEvidenceValue mono">
                                                {String(proofObj.drand_randomness || '').slice(0, 16)}
                                                {proofObj.drand_randomness ? '…' : ''}
                                              </div>
                                            </>
                                          )}

                                          {proofObj.seed_source === 'commit_reveal' && (
                                            <>
                                              <div className="winnerEvidenceLabel">Commit hash</div>
                                              <div className="winnerEvidenceValue mono">
                                                {String(proofObj.commit_hash || '').slice(0, 16)}
                                                {proofObj.commit_hash ? '…' : ''}
                                              </div>

                                              <div className="winnerEvidenceLabel">Reveal salt</div>
                                              <div className="winnerEvidenceValue mono">
                                                {String(proofObj.reveal_salt || '').slice(0, 16)}
                                                {proofObj.reveal_salt ? '…' : ''}
                                              </div>
                                            </>
                                          )}

                                          <div className="winnerEvidenceLabel">Hashed number</div>
                                          <div className="winnerEvidenceValue mono">
                                            {proofObj.digest_hex ? `${String(proofObj.digest_hex).slice(0, 16)}…` : '—'}
                                          </div>

                                          <div className="winnerEvidenceLabel">Number (first 32 bits)</div>
                                          <div className="winnerEvidenceValue mono">
                                            {Number.isFinite(Number(proofObj.u32)) ? String(proofObj.u32) : '—'}
                                          </div>

                                          <div className="winnerEvidenceLabel">Modulo</div>
                                          <div className="winnerEvidenceValue mono">
                                            {Number.isFinite(Number(proofObj.mod)) &&
                                            Number.isFinite(Number(proofObj.eligible_count))
                                              ? `${proofObj.u32} mod ${proofObj.eligible_count} = ${proofObj.mod}`
                                              : '—'}
                                          </div>

                                          <div className="winnerEvidenceLabel">Picked index</div>
                                          <div className="winnerEvidenceValue mono">
                                            {proofObj.picked_index != null && proofObj.eligible_count != null
                                              ? `(${proofObj.mod} + 1) = ${proofObj.picked_index}`
                                              : proofObj.picked_index ?? '—'}
                                          </div>

                                          <div className="winnerEvidenceLabel">Rule used</div>
                                          <div className="winnerEvidenceValue mono">pickedIndex = (number mod N) + 1</div>
                                        </div>

                                        <details style={{ marginTop: 10 }}>
                                          <summary style={{ cursor: 'pointer' }}>View raw proof</summary>
                                          <pre className="winnerEvidenceRaw">{JSON.stringify(proofObj, null, 2)}</pre>
                                        </details>
                                      </>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p style={{ marginTop: 12 }}>No winners yet.</p>
            )}

            </div>
          </div>
        </div>
      )}

      {/* Bidding Schedule Modal */}
      {schedOpen && (
        <div
          onClick={() => setSchedOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-title"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 16,
              width: '96%',
              maxWidth: 760,
              maxHeight: '80vh',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0,0,0,.25)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <h3 id="schedule-title" style={{ margin: 0 }}>
                Bidding Schedule
              </h3>
              <button onClick={() => setSchedOpen(false)} aria-label="Close schedule">
                ✖
              </button>
            </div>

            {/*
              Mobile scroll fix:
              - The modal container intentionally uses overflow:hidden to keep rounded corners.
              - We make THIS body scrollable so both the desktop table AND the mobile card layout can scroll.
              - iOS Safari needs -webkit-overflow-scrolling for smooth touch scrolling.
            */}
            <div
              className="schedModalBody"
              style={{
                marginTop: 12,
                maxHeight: 'calc(80vh - 72px)',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
                paddingRight: 4,
              }}
            >
              {schedLoading ? (
                <p style={{ marginTop: 0 }}>Loading…</p>
              ) : schedRows && schedRows.length ? (
                <>
                  {/* Desktop/tablet table */}
                  <div className="schedTableWrap" style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 14 }}>
                      <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee' }}>
                          {['Period', 'Start', 'End', 'Starting Bid'].map((h) => (
                            <th
                              key={h}
                              style={{ padding: '6px 8px', position: 'sticky', top: 0, background: '#fff' }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {schedRows.map((r) => (
                          <tr key={r.period} style={{ borderBottom: '1px solid #f3f3f3' }}>
                            <td style={{ padding: '8px' }}>{r.period}</td>
                            <td style={{ padding: '8px' }}>
                              {fmtScheduleGroup(r.start, 'dd LLL yyyy, h:mm a')}
                              {schedTimezone && (
                                <div className="subtle">
                                  Your time: {fmtScheduleLocal(r.start, 'dd LLL yyyy, h:mm a')}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '8px' }}>
                              {fmtScheduleGroup(r.end, 'dd LLL yyyy, h:mm a')}
                              {schedTimezone && (
                                <div className="subtle">
                                  Your time: {fmtScheduleLocal(r.end, 'dd LLL yyyy, h:mm a')}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '8px' }}>
                              {r.startingBid != null && Number.isFinite(Number(r.startingBid))
                                ? schedMoney(Number(r.startingBid))
                                : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="schedCards">
                    {schedRows.map((r) => (
                      <div key={r.period} className="schedCard">
                        <div className="schedCardTop">
                          <div className="schedPeriod">Period {r.period}</div>
                          <div className="schedBid">
                            {r.startingBid != null && Number.isFinite(Number(r.startingBid))
                              ? schedMoney(Number(r.startingBid))
                              : '—'}
                          </div>
                        </div>

                        <div className="schedRow">
                          <div className="schedLabel">Start</div>
                          <div className="schedValue">
                            {fmtScheduleGroup(r.start, 'dd LLL yyyy, h:mm a')}
                            {schedTimezone && (
                              <div className="subtle">
                                Your time: {fmtScheduleLocal(r.start, 'dd LLL yyyy, h:mm a')}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="schedRow">
                          <div className="schedLabel">End</div>
                          <div className="schedValue">
                            {fmtScheduleGroup(r.end, 'dd LLL yyyy, h:mm a')}
                            {schedTimezone && (
                              <div className="subtle">
                                Your time: {fmtScheduleLocal(r.end, 'dd LLL yyyy, h:mm a')}
                              </div>
                            )}
                          </div>
                        </div>

                        {schedTimezone && <div className="schedTzNote">Group timezone: {tzLabel(schedTimezone)}</div>}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p style={{ marginTop: 0 }}>No schedule available.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bid History Modal */}
      {(activeGroup?.id || activeGroup?.group_id) && (
        <BidHistoryModal
          key={`${activeGroup?.id || activeGroup?.group_id}-${bhOpen ? 'open' : 'closed'}`}
          groupId={activeGroup?.id || activeGroup?.group_id}
          open={bhOpen}
          onClose={() => setBhOpen(false)}
        />
      )}
    </div>
  );
}
